import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import classNames from 'classnames';
import ChecklistBlock from '@helios/ChecklistBlock';
import ButtonLink from '@helios/ButtonLink';
import PageHero from '@helios/PageHero';
import Layout from '@/components/Layout.js';
import StructuredData from '@/components/StructuredData.js';
import CTA from '@/components/CTA.js';
import ApplyNow from '@/components/ApplyNow.js';
import { employerSubdomains } from '@/utils/domains.js';
import { COST } from '@/utils/programCosts.js';
import { getNationalProgramPageSchema } from '@/utils/seo/siteSchemas.js';
import LeadFormLink from '@/components/LeadFormLink.js';
import PageBanner from '@/public/images/degree-landing.jpg';
import PageBannerMobile from '@/public/images/degree-landing-mobile.jpg';
import CourseSequence from '@/public/images/aaba-course-sequence.jpg';
import LeeSeanHuang from '@/public/images/prof-lee-sean-huang.jpg';
import DarienJohnson from '@/public/images/prof-6.jpg';
import TheresaWalsh from '@/public/images/professors/theresa-walsh_info.jpg';
import PlatFormSlug1 from '@/public/images/platform-slug-1.jpg';
import PlatFormSlug2 from '@/public/images/platform-slug-2.jpg';
import PlatFormSlug3 from '@/public/images/platform-slug-3.jpg';
import PlatFormSlug4 from '@/public/images/platform-slug-4.jpg';
import PlatFormSlug5 from '@/public/images/platform-slug-5.jpg';
import PlatFormSlug6 from '@/public/images/platform-slug-6.jpg';
import PlatFormSlug7 from '@/public/images/platform-slug-7.jpg';
import FAQAccordion from '@helios/FAQAccordion';
import FAQSection from '@helios/FAQSection';
import DataTable from '@helios/DataTable';
import FeatureTabs from '@helios/FeatureTabs';
import Grid from '@helios/Grid';
import ProfessorCard from '@helios/ProfessorCard';
import ProgramCost from '@helios/ProgramCost';
import StickyScrollLayout from '@helios/StickyScrollLayout';
import { StatCardGrid } from '@helios/StatCard';
import heroContentStyles from '@/styles/PageHeroContent.module.scss';
import programStyles from '@/styles/ProgramDetailPage.module.scss';

// The rendered widths, so next/image picks the right candidates: the course
// sequence at 1200px in its phone scroll frame (D16) and in the 840px section
// column from md, the experience images in the column
// below lg and in FeatureTabs' panel from lg, the professor photos one to a
// row below md, three from md, two from lg (beside the sticky nav) and three
// again from xl.
const COURSE_IMAGE_SIZES = '(max-width: 767px) 1200px, 840px';
const EXPERIENCE_IMAGE_SIZES = '(max-width: 991px) calc(100vw - 40px), 360px';
const PROFESSOR_IMAGE_SIZES = '(max-width: 767px) calc(100vw - 40px), (max-width: 991px) calc((100vw - 96px) / 3), (max-width: 1199px) calc((100vw - 568px) / 2), 262px';
export const getStaticPaths = async () => {
  return {
    paths: [...employerSubdomains, 'campus'].map(site => ({
      params: {
        site
      }
    })),
    fallback: false
  };
};
export async function getStaticProps({
  params
}) {
  return {
    props: {
      site: params.site
    }
  };
}
function OnlineAssociatesDegreeInBusiness(t0) {
  const $ = _c(62);
  const {
    site
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      stat: "2 years",
      description: "In 2 years you can have an Associate Degree in Business Administration."
    };
    t2 = {
      stat: "Live classes",
      description: "Meet live with professors and classmates twice each week."
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      stat: `${COST.quarterlyFlatRate}/quarter`,
      description: <>Full Pell Grant recipients can attend with little to no out-of-pocket tuition costs.<sup>1</sup></>,
      footnote: "[1] Financial aid is available to those who qualify. Eligibility is determined through the FAFSA and may vary by student."
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      id: "highlights",
      label: "Highlights",
      content: <section className={programStyles.section}><StatCardGrid columns={4} items={[t1, t2, t3, {
          stat: "Transfer",
          description: <>We'll provide assistance as you{" "}<Link href="/transfer">transfer<sup>2</sup></Link>{" "}into a university.</>,
          footnote: "[2] Transfer credit is always up to the discretion of the receiving institution."
        }]} /></section>
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h2>About the Campus Online Business Program</h2>;
    t6 = <p>The Campus online associate degree in business (AABA) can prepare you to work in nearly any industry and for nearly every kind of organization. Whether your passion is film, technology, sports, music, or finance, every industry needs highly trained, educated, and competent business leaders to grow.</p>;
    t7 = <p>Our business program covers core business topics like marketing, management, accounting, data analytics, and entrepreneurship. You'll also learn in-demand business skills like building eye-catching presentations, leveraging AI to streamline processes, creating effective spreadsheets, and delivering impactful feedback to employees. This knowledge can help you climb the corporate ladder, start your own business, or open up the doors to industries you may not have considered before.</p>;
    t8 = <p>An associate degree in business administration can be a great first step to give you the options for whatever's next. Campus business administration students have goals to:</p>;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      id: "overview",
      label: "Overview",
      content: <section className={programStyles.section}>{t5}{t6}{t7}{t8}<ChecklistBlock items={["Transfer hard-earned credits to a four-year university or institution.", "Start a business with the practical skills taught from Campus coursework.", "Launch a career and apply knowledge from our top professors to the workforce."]} /></section>
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <h2>What will I learn in this program?</h2>;
    t11 = <p>Business Administration courses cover the practical skills and knowledge you will need to effectively work in the industry. Students will learn foundational business management and entrepreneurship skills vital for success in any business environment. Some of the skills you will learn are:</p>;
    $[9] = t10;
    $[10] = t11;
  } else {
    t10 = $[9];
    t11 = $[10];
  }
  let t12;
  let t13;
  let t14;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ChecklistBlock items={["Compose clear, organized business documents, including professional letters and memos tailored to a specific audience", "Perform fundamental accounting and finance procedures including journalizing transactions, preparing financial statements, and completing bank reconciliations", "Create and present professional reports and presentations with appropriate data and visual aids", "Use professional software to organize, execute, maintain, and improve business operations", "Apply management, marketing, customer service, and interpersonal skills to business situations", "Model behavior that reflects confidence, competence, and professionalism"]} />;
    t13 = <h2>What are careers for Business Administration graduates?</h2>;
    t14 = <p>The U.S. Bureau of Labor Statistics reports a number of fast-growing, high-paying careers that a business degree can help get you ready for, including:</p>;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
  } else {
    t12 = $[11];
    t13 = $[12];
    t14 = $[13];
  }
  let t15;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      id: "outcomes",
      label: "Program outcomes",
      content: <section className={programStyles.section}>{t10}{t11}{t12}{t13}{t14}<ChecklistBlock items={["Advertising/Marketing Coordinator", "Financial Manager", "HR Specialist", "Management Analyst", "Public Relations Specialist", "Market Research Associate", "Financial Analyst"]} /></section>
    };
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <h2>Program Costs</h2>;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  let t17;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Link href="/admissions/financial-aid" target="_blank">tuition assistance</Link>;
    $[16] = t17;
  } else {
    t17 = $[16];
  }
  let t18;
  let t19;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <p>Grants and other forms of{" "}{t17}{" "}are available to those who qualify. To find out what you're eligible for, <LeadFormLink>contact</LeadFormLink> an admissions counselor.</p>;
    t19 = <h3>Tuition and Fees</h3>;
    $[17] = t18;
    $[18] = t19;
  } else {
    t18 = $[17];
    t19 = $[18];
  }
  let t20;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = [{
      label: "Admissions Registration Fee:",
      value: COST.registrationFee
    }, {
      label: "Enrollment Deposit:",
      value: COST.enrollmentDeposit
    }, {
      label: "Full Program Tuition:",
      value: COST.fullTuition
    }, {
      label: "Books and Supplies:",
      value: "Included in Tuition"
    }, {
      label: "Total:",
      value: COST.total
    }];
    $[19] = t20;
  } else {
    t20 = $[19];
  }
  let t21;
  let t22;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = [{
      key: "quarter",
      header: ""
    }, {
      key: "academic",
      header: "Academic Year*"
    }, {
      key: "calendar",
      header: "Calendar Year**"
    }];
    t22 = [{
      quarter: "Annual Cost",
      academic: COST.annualTuition,
      calendar: COST.annualTuitionFourQuarters
    }];
    $[20] = t21;
    $[21] = t22;
  } else {
    t21 = $[20];
    t22 = $[21];
  }
  let t23;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      id: "cost",
      label: "Cost",
      content: <section className={programStyles.section}>{t16}{t18}{t19}<ProgramCost summary={t20} table={<DataTable columns={t21} footer={t22} label="Tuition by quarter" rowHeader="quarter" rows={[1, 2, 3, 4].map(_temp)} />}><p>Students enrolled in 7–12 credits per quarter are charged a flat tuition rate of {COST.quarterlyFlatRate}. Students taking 6 credits or fewer are charged {COST.perCreditRate} per credit. Students enrolling in more than 12 credits are charged the flat rate of {COST.quarterlyFlatRate}, plus {COST.perCreditRate} per credit for each additional credit above 12.</p></ProgramCost><p><small><em>*Students completing three quarters per year will complete the program in 2.5 years.</em></small><br /><small><em>**Most Campus students attend classes during all four quarters of the year.</em></small></p><p><small><em>{`A one-time ${COST.enrollmentDeposit} enrollment deposit may be required, to reserve a student's seat in class. Refund eligibility and applicable terms vary by program and enrollment status. Additional details regarding enrollment deposit terms, deferrals, and refund conditions can be found in the Campus Catalog.`}</em></small></p><p><em>Financial aid is available to those who qualify. Eligibility is determined through the Free Application for Federal Student Aid (FAFSA) and other applicable requirements. Awards may vary based on financial need, program, enrollment status, and availability of funds. Campus does not guarantee financial aid or student loan approval.</em></p></section>
    };
    $[22] = t23;
  } else {
    t23 = $[22];
  }
  let t24;
  let t25;
  let t26;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <h2>Meet your professors.</h2>;
    t25 = <p>You'll benefit from access to professors who also teach at some of the best colleges in the country, including Princeton, Stanford, UT Austin, Howard, and more. Campus' business professors are subject experts who bring a variety of online and industry experience to the virtual classroom. Elevate your online learning experience through dynamic live sessions and optional office hours each week.</p>;
    t26 = <p><Link href="/professors">Learn more</Link> about our world-class faculty.</p>;
    $[23] = t24;
    $[24] = t25;
    $[25] = t26;
  } else {
    t24 = $[23];
    t25 = $[24];
    t26 = $[25];
  }
  let t27;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      base: 1,
      md: 3,
      lg: 2,
      xl: 3
    };
    $[26] = t27;
  } else {
    t27 = $[26];
  }
  let t28;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <ProfessorCard affiliation="UC Davis" detail="Also teaches at:" href="/professors/theresa-walsh" image={{
      src: TheresaWalsh,
      alt: "Theresa Walsh",
      sizes: PROFESSOR_IMAGE_SIZES
    }} layout="stacked" name="Theresa Walsh" nameAs="h6" />;
    $[27] = t28;
  } else {
    t28 = $[27];
  }
  let t29;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ProfessorCard affiliation="NYU" detail="Also teaches Business at:" href="/professors/lee-sean-huang" image={{
      src: LeeSeanHuang,
      alt: "Lee-sean Huang",
      sizes: PROFESSOR_IMAGE_SIZES
    }} layout="stacked" name="Lee-Sean Huang" nameAs="h6" />;
    $[28] = t29;
  } else {
    t29 = $[28];
  }
  let t30;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      id: "professors",
      label: "Professors",
      content: <section className={programStyles.section}>{t24}{t25}{t26}<Grid columns={t27} gap="md">{t28}{t29}<ProfessorCard affiliation="UCLA" detail="Also teaches Success at:" href="/professors/darien-johnson" image={{
            src: DarienJohnson,
            alt: "Darien Johnson",
            sizes: PROFESSOR_IMAGE_SIZES
          }} layout="stacked" name="Darien Johnson" nameAs="h6" /></Grid></section>
    };
    $[29] = t30;
  } else {
    t30 = $[29];
  }
  let t31;
  let t32;
  let t33;
  let t34;
  let t35;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <h2>Program courses</h2>;
    t32 = <h3>Degree overview</h3>;
    t33 = <p>Students in the online associate degree in business program must complete at least 96 credit hours in order to earn their A.A. in Business Administration. Below is an example sequence of courses for the program, on an eight-quarter graduating schedule.</p>;
    t34 = <p>Quarters are 12 weeks long, including one “syllabus” week, 10 weeks of live instruction, and one final assessment week.</p>;
    t35 = <p><i>Note: The actual sequence of courses may vary per student.</i></p>;
    $[30] = t31;
    $[31] = t32;
    $[32] = t33;
    $[33] = t34;
    $[34] = t35;
  } else {
    t31 = $[30];
    t32 = $[31];
    t33 = $[32];
    t34 = $[33];
    t35 = $[34];
  }
  let t36;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = classNames(programStyles["course-sequence"], programStyles["course-sequence--scroll"]);
    $[35] = t36;
  } else {
    t36 = $[35];
  }
  let t37;
  let t38;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <div aria-label="Example course sequence" className={t36} role="region" tabIndex={0}><Image alt="figure" sizes={COURSE_IMAGE_SIZES} src={CourseSequence} /></div>;
    t38 = <h3>Course descriptions</h3>;
    $[36] = t37;
    $[37] = t38;
  } else {
    t37 = $[36];
    t38 = $[37];
  }
  let t39;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = {
      id: "courses",
      label: "Courses",
      content: <section className={programStyles.section}>{t31}{t32}{t33}{t34}{t35}{t37}{t38}<FAQAccordion items={[{
          question: "ACCT.100 - Financial Accounting I",
          answer: "This course introduces the accounting student to fundamental accounting principles and standards as prescribed by Generally Accepted Accounting Principles (GAAP). The student learns accrual-based accounting for service industries, including the entire accounting cycle, and procedures for the internal control of cash. The student will also learn the technical procedure of completing the accounting cycle, special purchase journals, preparing financial statements, financial analysis, and interpretation."
        }, {
          question: "ACCT.200 - Financial Accounting II",
          answer: "This course builds upon accounting principles and concepts covered in Financial Accounting I. Topics include merchandising operations, internal controls, receivables, inventory, current liabilities, and fixed and intangible assets."
        }, {
          question: "AI.100 - AI for Business",
          answer: "Delve into the world of generative AI models, including transformers, adversarial networks, and reinforcement learning, to grasp their unique strengths and weaknesses. Explore the techniques behind AI, such as prompting, data analysis, and vector embeddings, enabling you to envision innovative business applications and devise novel corporate use cases or entrepreneurial ventures. Gain insights into the crucial decision of when to employ AI, considering ethical concerns like bias in areas like housing and hiring, as well as safeguarding user privacy and identity. Engage in insightful discussions about diverse viewpoints from stakeholders to make informed judgments on the application of AI."
        }, {
          question: "BUSN.105 - Introduction to Business",
          answer: "This course covers the various forms of business ownership, major business functions, and roles played by businesses. Other topics include marketing, management, human resources, and finance."
        }, {
          question: "BUSN.150 - Business & Professional Ethics",
          answer: "An examination of important ethical problems and issues as applied to various business and professional environments. Topics include job discrimination, corporate responsibility, environmental obligations, power, accountability, social responsibility, and professional codes of ethics."
        }, {
          question: "BUSN.275 - Strategic Management",
          answer: "This course is designed to integrate the areas of general business studies, including marketing, finance, and management. This course focuses on the competitive strategy of the firm, examining issues central to its long-term and short-term competitive position. Using case studies, students act in the roles of key decision-makers and solve problems related to the development or maintenance of the competitive advantage of the firm."
        }, {
          question: "COLL.140 - Purpose-Driven Life",
          answer: "Students learn the key skills and habits needed to succeed in college classes. The course teaches practical strategies to help learners develop personal responsibility, resilience, and the habit of self-reflection. Students use intentional goal-setting and self-reflection to connect their personal passions and skills with real-world career paths and college opportunities. Students also learn vital career-readiness and financial literacy skills to create flexible, achievable plans. By the end of this course, students feel prepared for lifelong learning and personal and professional success."
        }, {
          question: "COMM.110 - Business Skills: Presentation Skills",
          answer: "Students will get an introduction to business presentation tools and acquire the basics of audio and video production. Students will learn the basics of visual communication including concepts of hierarchy, typography, and color theory. Students will also develop their online professional presence in the form of a LinkedIn profile and begin building their professional networking through cold outreach and conducting a series of informational interviews."
        }, {
          question: "COMM.250 - Business Communications & Professional Development",
          answer: "A course that furthers each student's ability to communicate in business situations. Students enhance their writing styles by reviewing key concepts and by producing a variety of written communications including letters, memos, minutes, and short reports. Peer collaboration and oral presentations are required."
        }, {
          question: "ECON.130 - Principles of Microeconomics",
          answer: "An analysis of the economic principles underlying the behavior of individual consumers and business firms. The goal is to apply select microeconomic theories to real-world situations. Students will learn how markets work and gain an understanding of the role of economics in business and in public and private decision-making. Topics include: markets and prices, consumer and producer behavior, and risk and cost measurement, the implications of government intervention, technological innovation, the advantages and disadvantages of different market structures."
        }, {
          question: "ECON.180 - Principles of Macroeconomics",
          answer: "This course covers the fundamental principles of economic concepts, and the way in which they are used to make sound business decisions. Topics include macroeconomic modeling, monetary and fiscal policy, private sector components of aggregate demand, and macroeconomic synthesis and challenges for the future."
        }, {
          question: "ENGL.125 - English Composition",
          answer: "This course emphasizes the development and organization of expository prose through the writing of short and long compositions. Critical thinking, public speaking, and research skills are also introduced so that these skills may be applied throughout the curriculum. Students do peer editing of projects in collaborative groups."
        }, {
          question: "ENGL.200 - Advanced Composition",
          answer: "Skills learned in the first composition course are reinforced and amplified through more complex writing projects. Students continue to develop independence in preparing and organizing written materials through peer editing. Specific attention is given to the process of finding and working with information from a variety of sources in order to write a research paper. Assignments completed outside of class are required to be submitted in typed final form."
        }, {
          question: "ENTR.210 - Entrepreneurship",
          answer: "This course provides an overview of the components involved in starting, owning and operating a business. Course topics include: entrepreneurial characteristics, developing a business plan, financial and legal considerations, developing goals, management and leadership skills, the ethics necessary for success along with an overview of growth strategies."
        }, {
          question: "FIN.220 - Modern Finance",
          answer: "This course presents a study of various methods of financing public and private organizations. An analysis of common service should be implied \u2013 business finance, savings, and consumer finance are included. Financial planning and management of liquid assets are emphasized."
        }, {
          question: "GEN ED.150 - Introduction to Logic & Critical Thinking",
          answer: "A study of the rules of valid judging and reasoning, both inductive and deductive, in a traditional language centered context rather than a symbolic context. Logical analysis of both formal and informal fallacies and of the consistency and logical consequences of a given set of statements is applied to concrete problems. A research project is required."
        }, {
          question: "GOVT.110 - American Government",
          answer: "In this course, students will develop a fundamental understanding of American politics. They will learn how to provide analytical answers \u2013 using the most current methods of political science \u2013 about the operations of the United States political system. They will see the ways in which it does and does not operate as designed and become familiar with social science disputes about how it does (and whether it does) function as a representative democracy. And they will learn to participate in the system, taking a stand on civic issues of importance to them. Using the tools of a modern political scientist \u2013 statistical analysis, logical modeling and written argument \u2013 students will gain significant knowledge of how the U.S. government operates, and useful analytical skills they could transfer to any job requiring knowledge of large, complex organizations and institutions. This course is designed to accommodate students with little background in government or politics, so it is as appropriate for beginners as it is for students who want to deepen their understanding of government and civics."
        }, {
          question: "IT.125 - Spreadsheets & Databases",
          answer: "This course provides a comprehensive overview of spreadsheet skills. The course focuses on the creation, editing, formatting, electronic publication, and printing of standard spreadsheet documents. Students will explore efficient spreadsheet navigation, formulas, functions, using graphics, preparing charts, and performing numeric and financial data analysis. This course also teaches the student to analyze, evaluate, and design an effective database. The student will learn fundamental concepts associated with using database objects, as well as understand the underlying concepts and theories in designing a simple relational database."
        }, {
          question: "LAW.215 - Business Law",
          answer: "An introductory survey of the basics of contractual law, torts, and specific forms of contractual relationships. Also includes a review of the nature and kinds of commercial paper and the treatment of negotiated commercial paper including holders of due course. The law surrounding sales contracts and security devices will be covered."
        }, {
          question: "LIFE SCI.125 - Environmental Science and Ecological Living",
          answer: "This course provides an overview of environmental science and ecological living, with a practical emphasis. Topics include ecology, human impacts on the environment, and sustainable practices. Students will develop a strong foundation in environmental science principles and learn how to apply sustainable strategies in various contexts."
        }, {
          question: "MATH.125 - Quantitative Reasoning",
          answer: "Students develop their ability to use mathematical reasoning to solve real-life problems by engaging in the following topics: algebra, units of measurement and geometry, probability and statistics, ratios and proportions. The objective of this course is to prepare students for the sort of math necessary for success in their chosen area of study."
        }, {
          question: "MGMT.205 - Principles of Management",
          answer: "This course covers the analysis of the various theories of organization and management from the standpoint of organizational and individual effectiveness. The management functions of planning, organizing, directing, and controlling are thoroughly covered and discussed."
        }, {
          question: "MGMT.230 - Small Business Management",
          answer: "This course provides an overview of the components involved in starting, owning and operating a small business. Course topics include: Entrepreneurial characteristics, developing a business plan, financial and legal considerations, developing goals, management and leadership skills, the ethics necessary for success along with an overview of growth strategies."
        }, {
          question: "MRKT.160 - Principles of Marketing",
          answer: "This course introduces the basics of marketing including pricing, promotion, product development, advertising, and distribution strategies. In addition, the course presents current theory on consumer decision - making, customer service, international marketing, and market segmentation."
        }, {
          question: "MRKT.200 - Digital Marketing",
          answer: "Students will gain a fundamental understanding of the tools, techniques, and analytical methods associated with the creation, measurement, and improvement of digital marketing campaigns. Course topics will include content marketing, advertising, search engine optimization (SEO), webpage design best practices, email marketing, social media marketing and more as students work toward also obtaining a Digital Marketing Certificate issued by Hubspot."
        }]} variant="compact" /></section>
    };
    $[38] = t39;
  } else {
    t39 = $[38];
  }
  let t40;
  let t41;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = classNames(programStyles.section, programStyles["section--last"]);
    t41 = <h2>Student experience</h2>;
    $[39] = t40;
    $[40] = t41;
  } else {
    t40 = $[39];
    t41 = $[40];
  }
  let t42;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = {
      id: "live-classes",
      title: "Live classes",
      body: <p>All classes in the Campus Scholars programs are live so that you can directly interact with professors and peers. We keep things engaging by holding lively discussions and debates, breakout sessions, forums, and more.</p>,
      media: <Image alt="Image of a live class in the Campus Online Associate Degree in Business Administration" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug1} />,
      panelMedia: <Image alt="figure" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug1} />
    };
    $[41] = t42;
  } else {
    t42 = $[41];
  }
  let t43;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = {
      id: "tutoring",
      title: "Tutoring",
      body: <p>We provide students with unlimited access to 1 on 1 math and writing tutoring at no additional cost. Our tutoring services are accessible 24/7 so that you can get the academic support you need, whenever and wherever.</p>,
      media: <Image alt="Image of a live tutoring session held in Campuswire" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug2} />,
      panelMedia: <Image alt="figure" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug2} />
    };
    $[42] = t43;
  } else {
    t43 = $[42];
  }
  let t44;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = {
      id: "office-hours",
      title: "Office hours",
      body: <p>Benefit from unprecedented access to professors from world-class universities and accomplished TAs through live 1:1 office hours. Faculty are available every week so you can make connections, build rapport, and deepen your knowledge.</p>,
      media: <Image alt="Image of live office hours held in Campuswire" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug3} />,
      panelMedia: <Image alt="figure" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug3} />
    };
    $[43] = t44;
  } else {
    t44 = $[43];
  }
  let t45;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = {
      id: "success-coach",
      title: "Success coach",
      body: <p>Before classes begin, you'll be matched with a personal success coach who will serve as your confidant, cheerleader, and advisor all the way through graduation. They'll help you set and achieve your academic and professional goals during your time at Campus.</p>,
      media: <Image alt="Image of a success coach one-on-one held in Campuswire" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug4} />,
      panelMedia: <Image alt="figure" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug4} />
    };
    $[44] = t45;
  } else {
    t45 = $[44];
  }
  let t46;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = {
      id: "tech-pack",
      title: "Tech pack",
      body: <p>Campus students who need them receive a new laptop and mobile Wi-Fi access at no additional cost. We also cover all eTextbooks and digital course materials — so nothing stands between you and your degree.</p>,
      media: <Image alt="Image of a laptop, hotspot, and other Campus gear" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug5} />,
      panelMedia: <Image alt="figure" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug5} />
    };
    $[45] = t46;
  } else {
    t46 = $[45];
  }
  let t47;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = {
      id: "social-network",
      title: "Social network",
      body: <p>Through Campus, you'll meet like-minded individuals and create deep, meaningful relationships with your peers. Find lifelong friends who share similar interests (through student clubs) and meet new people at a variety of curated social events.</p>,
      media: <Image alt="Image of students communicating via chat rooms in Campuswire" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug6} />,
      panelMedia: <Image alt="figure" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug6} />
    };
    $[46] = t47;
  } else {
    t47 = $[46];
  }
  let t48;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = [t4, t9, t15, t23, t30, t39, {
      id: "experience",
      label: "Experience",
      content: <section className={t40}>{t41}<FeatureTabs items={[t42, t43, t44, t45, t46, t47, {
          id: "events",
          title: "Events",
          body: <p>Explore your career options and learn more about the journey to get there through exclusive Campus-hosted events featuring industry experts from influential companies like Google, TikTok, and Spotify. Also enjoy game nights, trivia nights, and themed social events.</p>,
          media: <Image alt="Image of a live guest speaker event" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug7} />,
          panelMedia: <Image alt="figure" sizes={EXPERIENCE_IMAGE_SIZES} src={PlatFormSlug7} />
        }]} label="Student experience" /></section>
    }];
    $[47] = t48;
  } else {
    t48 = $[47];
  }
  const steps = t48;
  let t49;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <Head><title>Online Associate Degree Program in Business | Campus.edu</title><link rel="canonical" href="https://campus.edu/online-associates-degree-in-business" /><meta httpEquiv="X-UA-Compatible" content="IE=Edge" /><meta name="description" content="Campus.edu offers an online associate degree in business administration that will help you launch your career. Apply to our business degree program today." /><meta name="keywords" content="Online Associate Degree in Business" /><meta name="viewport" content="width=device-width, initial-scale=1" /></Head>;
    $[48] = t49;
  } else {
    t49 = $[48];
  }
  let t50;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <StructuredData id="program-page-schema" data={getNationalProgramPageSchema("/online-associates-degree-in-business")} />;
    $[49] = t50;
  } else {
    t50 = $[49];
  }
  let t51;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <PageHero.Image src={PageBanner} mobileSrc={PageBannerMobile} alt="Online Associate Degree in Business Administration" priority={true} />;
    $[50] = t51;
  } else {
    t51 = $[50];
  }
  let t52;
  let t53;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <PageHero><PageHero.Media height="floating" overlay="scrim">{t51}<PageHero.Overlay><PageHero.Container><PageHero.Content tone="inverse"><h1 className={classNames(heroContentStyles.title, heroContentStyles["title--loose"], heroContentStyles["title--inverse"], heroContentStyles["title--floating"])}>Online Associate Degree in Business Administration.</h1><ButtonLink href="/admissions-application">Apply now</ButtonLink></PageHero.Content></PageHero.Container></PageHero.Overlay></PageHero.Media></PageHero>;
    t53 = <StickyScrollLayout container="wide" steps={steps} variant="wide" />;
    $[51] = t52;
    $[52] = t53;
  } else {
    t52 = $[51];
    t53 = $[52];
  }
  let t54;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = {
      question: "Is Campus accredited?",
      answer: <><p>Yes, Campus is an accredited college. Campus is accredited by the Accrediting Commission for Community and Junior Colleges (ACCJC), an institutional accrediting body recognized by the Council for Higher Education Accreditation and the U.S. Department of Education. Additional information about accreditation, including the filing of complaints against member institutions, can be found at{" "}<a href="https://accjc.org/" target="_blank" rel="noreferrer">www.accjc.org</a>{"."}</p></>
    };
    $[53] = t54;
  } else {
    t54 = $[53];
  }
  let t55;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = {
      question: "What is the cost of tuition?",
      answer: <><p>Tuition for all online Campus Scholars programs is{" "}{COST.annualTuition} per academic year.{" "}<Link href="/tuition">Learn more about tuition for the program</Link>{" "}and for options to potentially attend with no out-of-pocket costs for tuition.</p></>
    };
    $[54] = t55;
  } else {
    t55 = $[54];
  }
  let t56;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = {
      question: "Am I able to use federal financial aid?",
      answer: <><p>Our programs are eligible for federal financial aid to those who qualify. To learn more, check out our page on{" "}<Link href="/tuition">tuition</Link>.</p></>
    };
    $[55] = t56;
  } else {
    t56 = $[55];
  }
  let t57;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = {
      question: "How do I apply?",
      answer: <><p>Applicants are required to fill out a short{" "}<Link href="/admissions-application">application</Link>{" "}and complete an online interview with a member of our admissions team. Submissions of standardized test scores are not required.</p></>
    };
    $[56] = t57;
  } else {
    t57 = $[56];
  }
  let t58;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = {
      question: "Why is it called Campus?",
      answer: <><p>Because we're creating a new model for the college experience, we wanted a name that would both encapsulate and stand out from the thousands of other colleges and universities in the country.</p></>
    };
    $[57] = t58;
  } else {
    t58 = $[57];
  }
  let t59;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <p>The {COST.enrollmentDeposit} enrollment deposit is a{" "}<em>refundable commitment</em>{" that secures your spot in class. This allows us to better manage class sizes and ensure that students who reserve seats are truly ready to begin. By setting a lower deposit amount than what many colleges require, we're working to ensure that financial constraints are minimized for incoming students."}</p>;
    $[58] = t59;
  } else {
    t59 = $[58];
  }
  let t60;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <div id="wrapper">{t49}{t50}{t52}{t53}<FAQSection id="faq" title="FAQ"><FAQAccordion items={[t54, t55, t56, t57, t58, {
          question: "Why is there an enrollment deposit?",
          answer: <>{t59}<p>If you stay enrolled through the second week of your second term and attend at least one class during that period, the deposit will be{" "}<strong>fully refunded</strong>{". If you defer your enrollment, you don't need to pay again\u2014the original deposit still applies and can be refunded once you meet the criteria. The deposit is non-refundable for students who submit payment but do not enroll or fail to meet the above conditions."}</p></>
        }]} variant="compact" /></FAQSection><CTA /><ApplyNow /></div>;
    $[59] = t60;
  } else {
    t60 = $[59];
  }
  let t61;
  if ($[60] !== site) {
    t61 = <Layout site={site}>{t60}</Layout>;
    $[60] = site;
    $[61] = t61;
  } else {
    t61 = $[61];
  }
  return t61;
}
function _temp(quarter) {
  return {
    quarter: `Quarter ${quarter}`,
    academic: quarter === 4 ? "-" : COST.quarterlyFlatRate,
    calendar: COST.quarterlyFlatRate
  };
}
export default OnlineAssociatesDegreeInBusiness;